// VideoSectionClient.tsx
"use client";

import clsx from "clsx";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import ButtonLandingPage from "@/components/landing/ButtonLandingPage";
import { useSmartVideo } from "@/app/hooks/useSmartVideo";
const VIDEO_FILES = ["/5eventy.mp4", "/2video.mp4", "/3video.mp4", "/4video.mp4", "/1video.mp4", "/6gala.mp4", "/7video.mp4", "/8video.mp4", "/9video.mp4"] as const;
type Props = {
  heading: string;
  heading2: string;
};
function posterFromVideo(src: string) {
  return src.replace(/\.mp4$/i, ".webp");
}
function isIOS(): boolean {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || navigator.platform === "MacIntel" && (navigator as any).maxTouchPoints > 1;
}

/**
 * Globalny scheduler startów play() – zapobiega “thundering herd” (9 naraz).
 * Dzięki temu mniej ścina scroll i rzadziej “któreś nie startuje”.
 */
type QueueJob = {
  id: number;
  priority: number;
  run: () => Promise<void> | void;
};
const PLAY_QUEUE = (() => {
  let running = 0;
  const maxParallel = 1; // celowo 1 dla stabilności na mobile/Safari
  const jobs: QueueJob[] = [];
  let nextId = 1;
  const pump = () => {
    if (running >= maxParallel) return;
    if (jobs.length === 0) return;
    jobs.sort((a, b) => b.priority - a.priority);
    const job = jobs.shift();
    if (!job) return;
    running += 1;
    Promise.resolve().then(job.run).catch(() => {}).finally(() => {
      running -= 1;
      pump();
    });
  };
  return {
    enqueue(run: QueueJob["run"], priority: number) {
      const id = nextId++;
      jobs.push({
        id,
        priority,
        run
      });
      pump();
      return () => {
        const idx = jobs.findIndex(j => j.id === id);
        if (idx >= 0) jobs.splice(idx, 1);
      };
    }
  };
})();
const GridClip = memo(function GridClip({
  src,
  index,
  prefersReduced
}: {
  src: string;
  index: number;
  prefersReduced: boolean;
}) {
  const poster = posterFromVideo(src);

  // Mobile: pokazujemy tylko 1, 4, 7 (bez zmian)
  const isMobileVisible = index === 1 || index === 4 || index === 7;
  const isPriority = index === 0; // “główne” = pierwszy kafelek

  const {
    containerRef,
    videoRef,
    shouldLoad
  } = useSmartVideo(prefersReduced, {
    loadRootMargin: "800px 0px 800px 0px",
    playThreshold: 0.1,
    warmFrame: true,
    forcePreloadAutoOnLoad: true
  });
  const [hasMountedVideo, setHasMountedVideo] = useState(false);
  useEffect(() => {
    if (shouldLoad) setHasMountedVideo(true);
  }, [shouldLoad]);
  const [videoReady, setVideoReady] = useState(false);
  const mountedRef = useRef(false);
  const timeoutsRef = useRef<number[]>([]);
  const cancelQueuedPlayRef = useRef<null | (() => void)>(null);
  const lastPlayAttemptAtRef = useRef<number>(0);
  const clearTimers = useCallback(() => {
    timeoutsRef.current.forEach(t => window.clearTimeout(t));
    timeoutsRef.current = [];
  }, []);
  const scheduleTimeout = useCallback((fn: () => void, ms: number) => {
    const id = window.setTimeout(fn, ms);
    timeoutsRef.current.push(id);
  }, []);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearTimers();
      if (cancelQueuedPlayRef.current) cancelQueuedPlayRef.current();
      cancelQueuedPlayRef.current = null;
    };
  }, [clearTimers]);
  const doPlayBurst = useCallback(async () => {
    if (prefersReduced) return;
    const el = (videoRef as any)?.current as HTMLVideoElement | null;
    if (!el) return;

    // Anty-spam: jeśli próbowało przed chwilą, nie mielimy CPU.
    const now = Date.now();
    if (now - lastPlayAttemptAtRef.current < 350) return;
    lastPlayAttemptAtRef.current = now;

    // Safari lubi to przed play()
    el.muted = true;
    (el as any).playsInline = true;

    // Upewnij się, że preload auto (po mount)
    try {
      if (el.preload !== "auto") el.preload = "auto";
    } catch {
      // ignore
    }
    const tryPlayOnce = () => {
      if (!mountedRef.current) return;
      const p = el.play();
      if (p && typeof (p as any).catch === "function") (p as Promise<void>).catch(() => {});
    };

    // Krótka sekwencja retry – skuteczna na iOS
    tryPlayOnce();
    requestAnimationFrame(tryPlayOnce);
    scheduleTimeout(tryPlayOnce, 120);
    scheduleTimeout(tryPlayOnce, 320);
    scheduleTimeout(tryPlayOnce, 650);
    scheduleTimeout(tryPlayOnce, 1100);
  }, [prefersReduced, scheduleTimeout, videoRef]);
  const attemptPlay = useCallback(() => {
    if (prefersReduced) return;
    const el_0 = (videoRef as any)?.current as HTMLVideoElement | null;
    if (!el_0) return;
    if (document.visibilityState !== "visible") return;

    // Priority:
    // - pierwszy kafelek ma minimalny delay
    // - reszta startuje “falą” (mniej ścina UI)
    const baseDelay = isPriority ? 0 : Math.min(index * 140, 1100);

    // iOS: dajemy odrobinę większe rozproszenie startów (mniej “czkawki”)
    const iosExtra = isIOS() ? isPriority ? 0 : 120 : 0;

    // Kasujemy poprzednie zaplanowane starty
    clearTimers();
    if (cancelQueuedPlayRef.current) cancelQueuedPlayRef.current();
    cancelQueuedPlayRef.current = null;

    // Scheduler: kontroluje ile play() jednocześnie
    cancelQueuedPlayRef.current = PLAY_QUEUE.enqueue(async () => {
      if (!mountedRef.current) return;
      await new Promise<void>(r => scheduleTimeout(r, baseDelay + iosExtra));
      if (!mountedRef.current) return;
      await doPlayBurst();
    }, isPriority ? 100 : 10);
  }, [prefersReduced, videoRef, isPriority, index, clearTimers, scheduleTimeout, doPlayBurst]);

  // Podpinamy eventy dopiero gdy video jest zamontowane
  useEffect(() => {
    if (!hasMountedVideo) return;
    if (prefersReduced) return;
    const el_1 = (videoRef as any)?.current as HTMLVideoElement | null;
    if (!el_1) return;
    const onLoadedMetadata = () => attemptPlay();
    const onCanPlay = () => {
      setVideoReady(true);
      attemptPlay();
    };
    const onPlaying = () => setVideoReady(true);
    const onWaiting = () => {
      // przy mocnym obciążeniu potrafi przejść w waiting – próbujemy delikatnie wznowić
      attemptPlay();
    };
    const onStalled = () => {
      attemptPlay();
    };
    const onError = () => {
      setVideoReady(false);
      // Recovery: lekkie “odśwież” bufor, ale bez pętli agresji
      scheduleTimeout(() => {
        if (!mountedRef.current) return;
        try {
          el_1.load();
        } catch {
          // ignore
        }
        attemptPlay();
      }, isPriority ? 250 : 650);
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") attemptPlay();
    };
    el_1.addEventListener("loadedmetadata", onLoadedMetadata);
    el_1.addEventListener("canplay", onCanPlay);
    el_1.addEventListener("playing", onPlaying);
    el_1.addEventListener("waiting", onWaiting);
    el_1.addEventListener("stalled", onStalled);
    el_1.addEventListener("error", onError);
    document.addEventListener("visibilitychange", onVisibility);

    // Kick
    attemptPlay();
    return () => {
      el_1.removeEventListener("loadedmetadata", onLoadedMetadata);
      el_1.removeEventListener("canplay", onCanPlay);
      el_1.removeEventListener("playing", onPlaying);
      el_1.removeEventListener("waiting", onWaiting);
      el_1.removeEventListener("stalled", onStalled);
      el_1.removeEventListener("error", onError);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [attemptPlay, hasMountedVideo, prefersReduced, scheduleTimeout, videoRef, isPriority]);
  const preloadMode = useMemo(() => {
    // Priorytet: pierwszy szybciej startuje; reszta mniej agresywnie dopóki nie powinna się załadować
    if (!shouldLoad) return "metadata";
    return isPriority ? "auto" : "auto";
  }, [shouldLoad, isPriority]);
  return <div ref={containerRef as any} className={clsx("relative overflow-hidden bg-black size-full transform-gpu", !isMobileVisible && "hidden md:block")}>
            {/* Poster jako tło (bez zmian wizualnych) */}
            <div className="absolute inset-0" style={{
      backgroundImage: `url(${poster})`,
      backgroundSize: "cover",
      backgroundPosition: "center",
      transform: "translate3d(0,0,0)",
      backfaceVisibility: "hidden",
      WebkitBackfaceVisibility: "hidden"
    }} aria-hidden="true" />

            {hasMountedVideo && <video ref={videoRef as any} src={src} poster={poster} autoPlay muted loop playsInline preload={preloadMode} disablePictureInPicture controls={false} className="absolute inset-0 w-full h-full object-cover block" style={{
      transform: "translate3d(0,0,0) scale(1.02)",
      backfaceVisibility: "hidden",
      WebkitBackfaceVisibility: "hidden",
      willChange: "opacity",
      opacity: videoReady ? 1 : 0,
      transition: "opacity 300ms ease-out"
    }} onLoadedData={() => setVideoReady(true)} onPlaying={() => setVideoReady(true)} onError={() => setVideoReady(false)} />}

            <div className="absolute inset-0 bg-black/10 pointer-events-none" />
        </div>;
});
export default function VideoSectionClient(props: Partial<Props>) {
  const {
    heading,
    heading2
  } = {
    heading: "Organizujemy wyjazdy, które zbliżają ludzi.",
    heading2: "Organizujemy eventy, które poruszają emocje.",
    ...props
  };
  const sectionRef = useRef<HTMLElement>(null);
  const prefersReduced = useReducedMotion() ?? false;
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ["start start", "end end"]
  });

  // Animacja/wygląd bez zmian
  const scale = prefersReduced ? 1 : useTransform(scrollYProgress, [0, 1], [3.2, 1.0]);
  const h1Opacity = useTransform(scrollYProgress, [0.06, 0.14, 0.34, 0.42], [0, 1, 1, 0]);
  const h1Y = useTransform(scrollYProgress, [0.06, 0.14, 0.42], [30, 0, -30]);
  const h2Opacity = useTransform(scrollYProgress, [0.38, 0.48, 1.0], [0, 1, 1]);
  const h2Y = useTransform(scrollYProgress, [0.38, 0.48], [30, 0]);
  return <section ref={sectionRef} className="relative h-[350vh] bg-black">
            <div className="sticky top-0 h-screen overflow-hidden bg-black">
                {/* UI Layers */}
                <div className="relative z-[20] flex h-full items-center justify-center pointer-events-none">
                    <div className="w-full px-[5%] text-center">
                        <div className="relative mx-auto max-w-xl h-[30vh]">
                            <motion.div style={{
              opacity: h1Opacity,
              y: h1Y
            }} className="absolute inset-0 flex flex-col items-center justify-center">
                                <h2 className="text-3xl font-medium text-white md:text-[4rem] drop-shadow-lg">
                                    {heading}
                                </h2>
                                <div className="mt-10 pointer-events-auto">
                                    <ButtonLandingPage label="Napisz do nas" />
                                </div>
                            </motion.div>

                            <motion.div style={{
              opacity: h2Opacity,
              y: h2Y
            }} className="absolute inset-0 flex flex-col items-center justify-center">
                                <h2 className="text-3xl font-medium text-white md:text-[4rem] drop-shadow-lg">
                                    {heading2}
                                </h2>
                                <div className="mt-10 pointer-events-auto">
                                    <ButtonLandingPage label="Napisz do nas" />
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </div>

                {/* TŁO WIDEO */}
                <div className="absolute inset-0 z-0">
                    <div className="absolute inset-0 z-10 bg-black/40" />

                    <motion.div style={{
          scale
        }} className={clsx("size-full origin-center grid gap-2", "grid-cols-1 md:grid-cols-3")}>
                        {VIDEO_FILES.map((src, i) => <GridClip key={src} src={src} index={i} prefersReduced={prefersReduced} />)}
                    </motion.div>
                </div>

                <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-black to-transparent z-[5]" />
            </div>
        </section>;
}